import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CmsLoadState,
  CompletionCmsTexts,
  CompletionFormValues,
  CompletionOrgEmployment,
  CompletionType,
} from '../../types/Application/completion';
import { ValidatedField } from '../../types/validation';
import { validateField } from '../../validation';
import { validFileTypes } from '../../fileConfig';
import {
  generateMonthsArray,
  getDaysInMonth,
  getMonthName,
  getRequiredWorkingHours,
  getWorkedMonthsEmploymentCert,
  getWorkedMonthsWageSlips,
} from '../../Functions/Application';
import {
  ApplicationOrg,
  EmploymentByMonth,
  MetaDataEmployment,
} from '../../types/Application/application';
import {
  CompletionType as CompletionTypeEnum,
  SigningType,
  TypeOfFile,
} from '../../constants/Enum';

interface CompletionState {
  cmsLoadState: CmsLoadState;
  cms: CompletionCmsTexts;
  form: CompletionFormValues;
  employments: CompletionOrgEmployment;
  employmentAdded: boolean;
  employmentsByMonth: EmploymentByMonth[];
  metaDataEmployments: MetaDataEmployment[];
  signingType: SigningType | undefined;
}

// Define the initial state using that type
const initialState: CompletionState = {
  cmsLoadState: 'INITIAL',
  cms: {} as CompletionCmsTexts,
  form: {
    personalNumber: {
      name: 'personalNumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PERSONALNUMBER',
      formPaginationNumber: 1,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
        CompletionTypeEnum.Contact,
      ],
    },
    orgNumber: {
      name: 'orgNumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ORGNUMBER',
      formPaginationNumber: 2,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
        CompletionTypeEnum.Contact,
      ],
    },
    employedFrom: {
      name: 'employedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Employment],
      compareDate: '',
      compareType: 'LESS_THAN',
    },
    employedTo: {
      name: 'employedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Employment],
      compareDate: '',
      compareType: 'GREATER_THAN',
    },
    contactFirstname: {
      name: 'contactFirstname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Contact],
    },
    contactLastname: {
      name: 'contactLastname',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Contact],
    },
    contactTelephone: {
      name: 'contactTelephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Contact],
    },
    contactEmail: {
      name: 'contactEmail',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      formPaginationNumber: 2,
      completionTypes: [CompletionTypeEnum.Contact],
    },
    employmentFileInput: {
      name: 'employmentFileInput',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      files: {
        files: [],
        typeOfFile: TypeOfFile.EmploymentCertificate,
        validFileTypes: validFileTypes,
      },
      formPaginationNumber: 2,
      validFileTypes: validFileTypes,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
      ],
    },
    employmentWorkedPeriod: {
      name: 'employmentWorkedPeriod',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
      ],
    },
    telephone: {
      name: 'telephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      formPaginationNumber: 3,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
        CompletionTypeEnum.Contact,
      ],
    },
    email: {
      name: 'email',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      formPaginationNumber: 3,
      completionTypes: [
        CompletionTypeEnum.Document,
        CompletionTypeEnum.Employment,
        CompletionTypeEnum.Contact,
      ],
    },
  } as CompletionFormValues,
  employments: {},
  employmentsByMonth: [],
  metaDataEmployments: [],
  employmentAdded: false,
  signingType: undefined,
};

const completionSlice = createSlice({
  name: 'completion',
  initialState,
  reducers: {
    setStateToInitialState: () => {
      return { ...initialState };
    },
    setStateToStartSection: (state) => {
      const {
        contactEmail,
        contactLastname,
        contactFirstname,
        contactTelephone,
        employmentFileInput,
        employedFrom,
        employedTo,
        employmentWorkedPeriod,
        orgNumber,
      } = initialState.form;
      const employmentsInitial = initialState.employments;
      const employmentsByMonthInitail = initialState.employmentsByMonth;
      return {
        ...state,
        form: {
          ...state.form,
          contactEmail: contactEmail,
          contactFirstname: contactFirstname,
          contactLastname: contactLastname,
          contactTelephone: contactTelephone,
          employedFrom: employedFrom,
          employedTo: employedTo,
          employmentFileInput: employmentFileInput,
          employmentWorkedPeriod: employmentWorkedPeriod,
          orgNumber: orgNumber,
        },
        employments: employmentsInitial,
        employmentsByMonth: employmentsByMonthInitail,
      };
    },
    setStateToCompletionSection: (state) => {
      return { ...state };
    },
    validateAndSetFormValue: (state, action: PayloadAction<ValidatedField>) => {
      if (action.payload.validationType === 'FILE') {
        const { files, ...payloadWithoutFiles } = action.payload;
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof CompletionFormValues],
            payloadWithoutFiles,
            isValid: validateField({
              ...state.form[action.payload.name as keyof CompletionFormValues],
              ...action.payload,
            }),
          },
        };
      } else {
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof CompletionFormValues],
            ...action.payload,
            isValid: validateField({
              ...state.form[action.payload.name as keyof CompletionFormValues],
              ...action.payload,
            }),
          },
        };
      }
    },
    clearFieldsEmploymentForm: (state) => {
      const { form } = state;
      const fieldNames: string[] = [
        form.employedFrom.name,
        form.employedTo.name,
        form.employmentWorkedPeriod.name,
        form.orgNumber.name,
        form.employmentFileInput.name,
      ];
      fieldNames.forEach((fieldName) => {
        const field = {
          name: fieldName,
          value: '',
          isDirty: false,
        } as ValidatedField;
        state.form = {
          ...state.form,
          [field.name]: {
            ...state.form[field.name as keyof CompletionFormValues],
            ...field,
            isValid: validateField({
              ...state.form[field.name as keyof CompletionFormValues],
              ...field,
            }),
          },
        };
      });
      // state.form.employmentFileInput = initialState.form.employmentFileInput;
    },
    clearEmploymentFileInputField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          employmentFileInput: initialState.form.employmentFileInput,
        },
      };
    },
    clearEmploymentWorkedPeriodField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          employmentWorkedPeriod: initialState.form.employmentWorkedPeriod,
        },
      };
    },
    clearFieldsContactForm: (state) => {
      const { form } = state;
      const fieldNames: string[] = [
        form.contactEmail.name,
        form.contactFirstname.name,
        form.contactLastname.name,
        form.contactTelephone.name,
        form.orgNumber.name,
      ];
      fieldNames.forEach((fieldName) => {
        const field = {
          name: fieldName,
          value: '',
          isDirty: false,
        } as ValidatedField;
        state.form = {
          ...state.form,
          [field.name]: {
            ...state.form[field.name as keyof CompletionFormValues],
            ...field,
            isValid: validateField({
              ...state.form[field.name as keyof CompletionFormValues],
              ...field,
            }),
          },
        };
      });
    },
    clearEmploymentAdded: (state) => {
      state.employmentAdded = false;
    },
    deleteEmployment: (state, action: PayloadAction<string>) => {
      delete state.employments[action.payload];
    },
    addEmployment: (
      state,
      action: PayloadAction<{
        employment: ApplicationOrg;
        completionType: CompletionType;
      }>
    ) => {
      if (
        action.payload.completionType === CompletionTypeEnum.Document ||
        action.payload.completionType === CompletionTypeEnum.Employment
      ) {
        if (
          !action.payload.employment.employedFrom ||
          !action.payload.employment.employedTo
        )
          return;
        if (action.payload.employment.wageSlip !== undefined) {
          state.employments[action.payload.employment.nodeId] = {
            ...action.payload.employment,
            wageSlip: action.payload.employment.wageSlip,
            months: getWorkedMonthsWageSlips(
              action.payload.employment.wageSlip,
              action.payload.employment.employedFrom,
              action.payload.employment.employedTo
            ),
          };
        }
        if (action.payload.employment.employmentCert !== undefined) {
          state.employments[action.payload.employment.nodeId] = {
            ...action.payload.employment,
            employmentCert: action.payload.employment.employmentCert,
            months: getWorkedMonthsEmploymentCert(
              action.payload.employment.employedFrom,
              action.payload.employment.employedTo
            ),
          };
        }
      }
      if (action.payload.completionType === CompletionTypeEnum.Contact) {
        state.employments[action.payload.employment.nodeId] = {
          ...action.payload.employment,
          contact: action.payload.employment.contact,
        };
      }

      state.employmentAdded = true;
    },
    saveEditedEmployment: (state, action: PayloadAction<ApplicationOrg>) => {
      if (state.employments[action.payload.nodeId] !== undefined) {
        if (!action.payload.employedFrom || !action.payload.employedTo) {
          // if null it will be a contact completion employment
          state.employments[action.payload.nodeId] = action.payload;
        } else {
          if (action.payload.wageSlip !== undefined) {
            state.employments[action.payload.nodeId] = {
              ...action.payload,
              wageSlip: action.payload.wageSlip,
              months: getWorkedMonthsWageSlips(
                action.payload.wageSlip,
                action.payload.employedFrom,
                action.payload.employedTo
              ),
            };
          }
          if (action.payload.employmentCert !== undefined) {
            state.employments[action.payload.nodeId] = {
              ...action.payload,
              employmentCert: action.payload.employmentCert,
              months: getWorkedMonthsEmploymentCert(
                action.payload.employedFrom,
                action.payload.employedTo
              ),
            };
          }
        }
      }
    },
    setEmploymentsByMonth: (state) => {
      const employments = Object.values(state.employments);
      const workedMonthsArr: EmploymentByMonth[] = [];

      let prevDate: Date | undefined;

      for (let i = 0; i > -24; i--) {
        let currentDate: undefined | Date = undefined;
        if (prevDate === undefined) {
          currentDate = new Date();
          //setMonth() might return the same month or skip a month.
          //We instead use the 15th for rendering the calendar view since the date of month does not matter here.
          currentDate.setDate(15);
        } else {
          currentDate = prevDate;
          currentDate.setMonth(prevDate.getMonth() - 1);
        }
        let currentMonth = currentDate.getMonth();
        let currentYear = currentDate.getFullYear();
        workedMonthsArr.push({
          id: `${currentMonth}-${currentYear}-${Date.now().toString()}`,
          month: currentMonth + 1,
          monthText: `${getMonthName(currentMonth + 1)} ${currentYear}`,
          year: currentYear,
          expectedHours: getRequiredWorkingHours(
            getDaysInMonth(currentYear, currentMonth + 1)
          ),
          totalHoursWorked: NaN,
          monthHasEmploymentCert: false,
          isEmptyMonth: true,
        });
        prevDate = currentDate;
      }

      employments.forEach((employment) => {
        if (employment.wageSlip !== undefined) {
          employment.wageSlip.forEach((wageSlip) => {
            const existingMonthYear = workedMonthsArr.find((monthYear) => {
              return (
                monthYear.year === wageSlip.year &&
                monthYear.month === wageSlip.month
              );
            });
            if (existingMonthYear !== undefined) {
              existingMonthYear.id = `${employment.nodeId}-${wageSlip.month}-${wageSlip.year}`;
              existingMonthYear.monthHasEmploymentCert = false;

              if (existingMonthYear.totalHoursWorked) {
                existingMonthYear.totalHoursWorked += wageSlip.hoursWorked;
              } else {
                existingMonthYear.totalHoursWorked = wageSlip.hoursWorked;
              }
              existingMonthYear.isEmptyMonth = false;
              if (existingMonthYear.employments !== undefined) {
                existingMonthYear.employments.push({
                  worksiteName: employment.parentCompany.orgName,
                  nodeId: employment.nodeId,
                  hoursWorked: wageSlip.hoursWorked ?? undefined,
                });
              } else {
                existingMonthYear.employments = [
                  {
                    worksiteName: employment.parentCompany.orgName,
                    nodeId: employment.nodeId,
                    hoursWorked: wageSlip.hoursWorked ?? undefined,
                  },
                ];
              }
            }
          });
        } else if (employment.employmentCert !== undefined) {
          if (!employment.employedFrom || !employment.employedTo) return;
          const monthsArray = generateMonthsArray(
            employment.employedFrom,
            employment.employedTo
          );
          monthsArray.forEach((empMonthYear) => {
            var existingMonthYear = workedMonthsArr.find((monthYear) => {
              return (
                monthYear.year === empMonthYear.year &&
                monthYear.month === empMonthYear.month
              );
            });
            if (existingMonthYear !== undefined) {
              existingMonthYear.id = `${employment.nodeId}-${empMonthYear.month}-${empMonthYear.year}`;
              existingMonthYear.monthHasEmploymentCert = true;
              existingMonthYear.totalHoursWorked =
                existingMonthYear.totalHoursWorked ?? 0;
              existingMonthYear.isEmptyMonth = false;
              if (existingMonthYear.employments !== undefined) {
                existingMonthYear.employments.push({
                  worksiteName: employment.parentCompany.orgName,
                  nodeId: employment.nodeId,
                  hoursWorked: undefined,
                });
              } else {
                existingMonthYear.employments = [
                  {
                    worksiteName: employment.parentCompany.orgName,
                    nodeId: employment.nodeId,
                    hoursWorked: undefined,
                  },
                ];
              }
            }
          });
        }
      });
      state.employmentsByMonth = workedMonthsArr;
    },
    setSigningType: (state, action: PayloadAction<SigningType>) => {
      return { ...state, signingType: action.payload };
    },
  },
});

export const {
  setStateToInitialState,
  validateAndSetFormValue,
  clearFieldsEmploymentForm,
  clearEmploymentFileInputField,
  clearEmploymentWorkedPeriodField,
  clearFieldsContactForm,
  clearEmploymentAdded,
  deleteEmployment,
  addEmployment,
  saveEditedEmployment,
  setEmploymentsByMonth,
  setStateToStartSection,
  setStateToCompletionSection,
  setSigningType,
} = completionSlice.actions;
export default completionSlice.reducer;
